/**
 * The one definition of how a semantic colour scale is built.
 *
 * These percentages are consumed in three places that must never disagree:
 *
 *   tailwind.config.ts   emits the CSS `color-mix()` for every shade
 *   ThemeProvider.tsx    derives `-strong` against the same tint it will sit on
 *   ThemeTab.tsx         previews what a chosen colour will actually look like
 *
 * They were duplicated in all three, which meant a Settings preview could show
 * the admin a colour the app would never render. That is worse than no preview,
 * so the numbers live here and everything else imports them.
 *
 * This module is deliberately free of DOM and React so the Tailwind config, the
 * browser and the test runner can all load it.
 */

/** Soft tints: `color-mix(in srgb, base N%, surface)`. */
export const TINT_PERCENTS = {
  50: 8,
  100: 14,
  200: 28,
  300: 45,
  400: 70,
} as const;

/**
 * The heaviest tint a shade in the 50–100 range produces, as a 0–1 weight.
 * `-strong` is measured against this rather than against the bare surface,
 * because the real pattern is `bg-danger-100` + `text-danger-700`, not text on
 * a plain surface.
 */
export const TINT_BACKDROP = TINT_PERCENTS[100] / 100;

/**
 * Deep shades for hover and active states: `color-mix(in srgb, pole N%, strong)`.
 * Shades 800–950 used to be aliases of 700, which made `hover:bg-danger-800` on
 * a `bg-danger-700` button a no-op.
 */
export const DEEPEN_PERCENTS = {
  800: 12,
  900: 25,
  950: 40,
} as const;

/** Smallest sRGB channel shift a person reliably notices on a large filled area. */
export const MIN_HOVER_DELTA = 6;

/** WCAG AA threshold for normal-size text. */
export const AA_NORMAL = 4.5;

/** WCAG AA threshold for large text and non-text contrast. */
export const AA_LARGE = 3;

/** Is this a well-formed "R G B" triplet? */
export function isTriplet(value: string): boolean {
  return /^\d+\s+\d+\s+\d+$/.test(value.trim());
}

/** Linear blend of two "R G B" triplets. `weight` is how much of `b` to mix in. */
export function mixTriplets(a: string, b: string, weight: number): string {
  const pa = a.trim().split(/\s+/).map(Number);
  const pb = b.trim().split(/\s+/).map(Number);
  return pa.map((channel, i) => Math.round(channel + (pb[i] - channel) * weight)).join(" ");
}

/** Relative luminance of an "R G B" triplet, per WCAG 2.1. */
export function relativeLuminance(triplet: string): number {
  const [r, g, b] = triplet
    .trim()
    .split(/\s+/)
    .map((part) => {
      const value = Number.parseInt(part, 10) / 255;
      return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two "R G B" triplets. */
export function contrastRatio(a: string, b: string): number {
  const first = relativeLuminance(a);
  const second = relativeLuminance(b);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

/** Convert a hex string or triplet to an "R G B" triplet, or null if unparseable. */
export function toTriplet(value: string): string | null {
  const raw = String(value ?? "").trim();
  if (isTriplet(raw)) return raw;
  const hex = raw.replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16),
  ].join(" ");
}

/** Convert an "R G B" triplet to `#rrggbb`, or null if unparseable. */
export function toHex(triplet: string): string | null {
  if (!isTriplet(triplet)) return null;
  return `#${triplet
    .trim()
    .split(/\s+/)
    .map((p) => Number.parseInt(p, 10).toString(16).padStart(2, "0"))
    .join("")}`;
}

/** CSS `rgb()` string for an "R G B" triplet, for inline style previews. */
export function cssRgb(triplet: string): string {
  return `rgb(${triplet.trim().split(/\s+/).join(", ")})`;
}

/** The tinted background a shade in the 50–400 range resolves to. */
export function tintShade(base: string, surface: string, shade: keyof typeof TINT_PERCENTS): string {
  return mixTriplets(surface, base, TINT_PERCENTS[shade] / 100);
}

/**
 * The same colour, pushed until it is readable **as text**.
 *
 * `strongVariant` below produces a FILL and stops as soon as the colour clears
 * AA against BOTH the plain surface and the 100-tint of itself. That dual
 * constraint is why it sometimes stops short of what a *sentence* needs: it is
 * balancing two backdrops at once, and it gives up after a 90% blend rather
 * than destroy the hue.
 *
 * This derivation has one job and one backdrop, so it can go further. It walks
 * towards the pole away from the surface in the same 5% steps, and returns
 * `base` untouched when the colour already clears the bar — which is most of
 * dark mode, where a light state colour on a dark surface reads fine already.
 */
export function inkVariant(base: string, surface: string, target: number = AA_NORMAL): string {
  if (!isTriplet(base) || !isTriplet(surface)) return base;
  if (contrastRatio(base, surface) >= target) return base;

  const pole = relativeLuminance(surface) > 0.5 ? "0 0 0" : "255 255 255";
  for (let mixIn = 5; mixIn <= 100; mixIn += 5) {
    const candidate = mixTriplets(base, pole, mixIn / 100);
    if (contrastRatio(candidate, surface) >= target) return candidate;
  }
  return pole;
}

/* ── derivations ThemeProvider publishes as CSS variables ────────────────── */
/*
 * These four were defined inside ThemeProvider.tsx. They are pure colour maths
 * with no React and no DOM, and they were moved here for two reasons:
 *
 *   1. `strongVariant` and `inkVariant` are siblings — one produces the fill
 *      scale, the other the text scale — and a reader cannot judge either
 *      without seeing the other. Splitting them across two files is how the
 *      scales drift apart.
 *
 *   2. ThemeProvider.tsx is a "use client" module with JSX and path aliases,
 *      so no plain node script can load it. The audit scripts therefore kept a
 *      hand-written *approximation* of `strongVariant` — and it disagreed with
 *      the real one on the threshold (3 vs AA_NORMAL), on the constraints (one
 *      backdrop vs two) and on the blend range (60% vs 90%). Measurements taken
 *      against that copy described a product that did not exist. Living here,
 *      the real function is loadable by the audits, so that class of mistake
 *      cannot recur.
 *
 * ThemeProvider re-exports all four, so existing imports keep working.
 */

const FOREGROUND_LIGHT = { triplet: "255 255 255", hex: "#ffffff" };
const FOREGROUND_DARK = { triplet: "15 23 42", hex: "#0f172a" };

/**
 * Pick white or near-black text for a given background colour, whichever
 * yields the higher contrast ratio. Falls back to white when the triplet
 * cannot be parsed, matching the original hard-coded behaviour.
 */
export function readableForeground(triplet: string): { triplet: string; hex: string } {
  if (!isTriplet(triplet)) return FOREGROUND_LIGHT;
  const onLight = contrastRatio(triplet, FOREGROUND_LIGHT.triplet);
  const onDark = contrastRatio(triplet, FOREGROUND_DARK.triplet);
  return onDark > onLight ? FOREGROUND_DARK : FOREGROUND_LIGHT;
}

/**
 * Derive a high-contrast variant of a state colour for use as a FILL, and for
 * text placed on a surface tinted with that same colour (`bg-danger-50` +
 * `text-danger-700`).
 *
 * The raw state colour is chosen to look right as a solid fill, so it is often
 * too close to the surface to read — `--color-warning` on white is the typical
 * failure. This walks the colour away from the surface (towards black on a
 * light surface, towards white on a dark one) until it clears AA against BOTH
 * the plain surface and the 100-tint of itself, so one value serves both modes.
 *
 * Returns the input unchanged when it already passes, and never blends past 90%
 * so the hue stays recognisable — which also means it can return a value that
 * misses the bar, when no blend within that range satisfies both constraints.
 */
export function strongVariant(base: string, surface: string): string {
  if (!isTriplet(base) || !isTriplet(surface)) return base;

  // A light surface needs a darker colour, a dark surface needs a lighter one.
  const target = relativeLuminance(surface) > 0.5 ? "0 0 0" : "255 255 255";
  // Worst-case backdrop this colour will ever be read on.
  const backdrop = mixTriplets(surface, base, TINT_BACKDROP);

  let candidate = base;
  for (let weight = 0; weight <= 0.9; weight += 0.05) {
    candidate = mixTriplets(base, target, weight);
    if (
      contrastRatio(candidate, surface) >= AA_NORMAL &&
      contrastRatio(candidate, backdrop) >= AA_NORMAL
    ) {
      return candidate;
    }
  }
  return candidate;
}

/** Must match the `800` step in `semanticScale()` in tailwind.config.ts. */
const HOVER_MIX = DEEPEN_PERCENTS[800] / 100;

function maxChannelDelta(a: string, b: string): number {
  const first = a.trim().split(/\s+/).map(Number);
  const second = b.trim().split(/\s+/).map(Number);
  return Math.max(...first.map((channel, i) => Math.abs(channel - second[i])));
}

/**
 * Choose which end of the range shades 800–950 should travel towards.
 *
 * Hover normally wants to move FURTHER from the surface — darker on a light
 * page, lighter on a dark one. But a button can already be pinned against that
 * end: in the olive-green preset the primary colour is nearly as dark as the
 * body text, so pushing it darker moves it about two sRGB units and the button
 * feels dead under the cursor.
 *
 * CSS `color-mix()` cannot make that decision because it cannot branch on
 * luminance, so the theme decides here and publishes the answer as a variable.
 * The config then just interpolates towards whatever pole it is handed, which
 * keeps the whole scale declarative and still leaves Settings in control.
 */
export function deepPole(strong: string, surface: string): string {
  const BLACK = "0 0 0";
  const WHITE = "255 255 255";
  const away = relativeLuminance(strong) < relativeLuminance(surface) ? BLACK : WHITE;
  if (maxChannelDelta(strong, mixTriplets(strong, away, HOVER_MIX)) >= MIN_HOVER_DELTA) return away;
  // No room left in that direction, so the hover has to move the other way.
  return away === BLACK ? WHITE : BLACK;
}

/** Absolute darkness ceiling for the scrim, used on light backgrounds. */
const SCRIM_MAX_LUMINANCE = 0.06;
/** How much darker than the background the scrim must be on dark backgrounds. */
const SCRIM_DARKEN_RATIO = 0.35;

/**
 * Build the modal / video-wall scrim for a given page background.
 *
 * A scrim only works if it is DARKER than whatever it covers. An absolute
 * "make it dark" rule is not enough: a near-black overlay is still lighter than
 * a dark preset's `--color-bg`, so the backdrop would glow when a modal opens.
 *
 * So the target is relative: darken the background's own colour (keeping its
 * hue) until it is comfortably below the background's luminance, and never
 * above an absolute ceiling. The result is used as `bg-th-overlay/40`.
 */
export function scrimColor(bg: string): string {
  if (!isTriplet(bg)) return "15 23 42";

  const ceiling = Math.min(SCRIM_MAX_LUMINANCE, relativeLuminance(bg) * SCRIM_DARKEN_RATIO);

  let candidate = bg;
  for (let weight = 0; weight <= 1; weight += 0.02) {
    candidate = mixTriplets(bg, "0 0 0", weight);
    if (relativeLuminance(candidate) <= ceiling) return candidate;
  }
  return "0 0 0";
}
